import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { cssMap } from '@compiled/react';

import Button, { IconButton } from '@atlaskit/button/new';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import QuestionCircleIcon from '@atlaskit/icon/core/question-circle';
import SendIcon from '@atlaskit/icon/core/send';
import AtlassianIntelligenceIcon from '@atlaskit/icon/core/atlassian-intelligence';
import { Flex, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { getSourceIconStack } from './getSourceIcon.tsx';
import { SourceItem } from './SourceItem.tsx';
import type { PopularQuestion } from './popularQuestionsTypes.ts';

type QuestionItemProps = {
	question: PopularQuestion;
	isExpanded: boolean;
	onToggle: (id: string) => void;
	onFollowUp?: (followUpQuestion: string, parentQuestion: PopularQuestion) => void;
	onAskRovo?: (customQuestion: string, parentQuestion: PopularQuestion) => void;
};

export const QuestionItem = ({
	question,
	isExpanded,
	onToggle,
	onFollowUp,
	onAskRovo,
}: QuestionItemProps) => {
	const { formatMessage } = useIntl();
	const [rovoInput, setRovoInput] = useState('');
	const [sourcesExpanded, setSourcesExpanded] = useState(false);

	const handleToggle = useCallback(() => {
		onToggle(question.id);
	}, [onToggle, question.id]);

	const handleFollowUp = useCallback(
		(followUpQuestion: string) => {
			onFollowUp?.(followUpQuestion, question);
		},
		[onFollowUp, question],
	);

	const handleSendRovo = useCallback(() => {
		if (rovoInput.trim()) {
			onAskRovo?.(rovoInput.trim(), question);
			setRovoInput('');
		}
	}, [rovoInput, onAskRovo, question]);

	const handleRovoKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.key === 'Enter') {
				e.preventDefault();
				handleSendRovo();
			}
		},
		[handleSendRovo],
	);

	return (
		<div>
			<button
				type="button"
				css={[styles.questionRow, isExpanded && styles.questionRowExpanded]}
				onClick={handleToggle}
				aria-expanded={isExpanded}
			>
				<div css={styles.questionIcon}>
					<QuestionCircleIcon label="" color={token('color.icon.subtle')} />
				</div>
				<div css={styles.questionText}>
					<Text size="medium" weight="medium">
						{question.question}
					</Text>
				</div>
				<div css={styles.chevron}>
					{isExpanded ? (
						<ChevronUpIcon label={formatMessage(i18n.collapse)} color={token('color.icon')} />
					) : (
						<ChevronDownIcon label={formatMessage(i18n.expand)} color={token('color.icon')} />
					)}
				</div>
			</button>

			{isExpanded && (
				<div css={styles.expandedContent}>
					<Stack space="space.200">
						<Stack space="space.100">
							<div css={styles.sectionLabel}>
								<Text size="small" weight="semibold" color="color.text.subtle">
									{formatMessage(i18n.answerLabel)}
								</Text>
							</div>
							<Text size="medium">{question.answer}</Text>
						</Stack>

						{question.sources.length > 0 && (
							<div css={styles.sectionLabel}>
								<Inline spread="space-between">
									<Button onClick={() => setSourcesExpanded(!sourcesExpanded)}>
										<Flex gap="space.075" alignItems="center">
											{getSourceIconStack(question.sources)}
											<Text size="small" color="color.text.subtle">
												{formatMessage(i18n.sourcesCount, {
													count: question.sources.length,
												})}
											</Text>
										</Flex>
									</Button>
									<IconButton
										icon={sourcesExpanded ? ChevronUpIcon : ChevronDownIcon}
										label={formatMessage(
											sourcesExpanded ? i18n.collapseSources : i18n.expandSources,
										)}
										appearance="subtle"
										onClick={() => setSourcesExpanded(!sourcesExpanded)}
									/>
								</Inline>
								{sourcesExpanded && (
									<>
										<div css={styles.sourcesDivider} />
										<Stack space="space.200">
											{question.sources.map((source) => (
												<SourceItem key={source.url} source={source} />
											))}
										</Stack>
									</>
								)}
							</div>
						)}

						{question.followUpQuestions.length > 0 && (
							<Flex gap="space.075" wrap="wrap">
								{question.followUpQuestions.map((followUp) => (
									<button
										key={followUp}
										type="button"
										css={styles.followUpButton}
										onClick={() => handleFollowUp(followUp)}
									>
										<QuestionCircleIcon
											label=""
											spacing="none"
											color={token('color.icon.subtle')}
										/>
										<Text size="small">{followUp}</Text>
									</button>
								))}
							</Flex>
						)}

						<div css={styles.rovoInputContainer}>
							<div css={styles.rovoIcon}>
								<AtlassianIntelligenceIcon label="" color={token('color.icon.brand')} />
							</div>
							<input
								type="text"
								css={styles.rovoInput}
								placeholder={formatMessage(i18n.askRovoPlaceholder)}
								value={rovoInput}
								onChange={(e) => setRovoInput(e.target.value)}
								onKeyDown={handleRovoKeyDown}
							/>
							<IconButton
								icon={SendIcon}
								label={formatMessage(i18n.sendQuestion)}
								appearance="primary"
								shape="circle"
								spacing="compact"
								isDisabled={!rovoInput.trim()}
								onClick={handleSendRovo}
							/>
						</div>
					</Stack>
				</div>
			)}
		</div>
	);
};

const i18n = defineMessages({
	expand: {
		id: 'space-overview.smart-space-overview.popular-questions.expand',
		defaultMessage: 'Expand question',
		description: 'Accessible label for the button to expand a popular question.',
	},
	collapse: {
		id: 'space-overview.smart-space-overview.popular-questions.collapse',
		defaultMessage: 'Collapse question',
		description: 'Accessible label for the button to collapse a popular question.',
	},
	answerLabel: {
		id: 'space-overview.smart-space-overview.popular-questions.answer-label',
		defaultMessage: 'Answer',
		description: 'Label for the answer section of a popular question.',
	},
	sourcesCount: {
		id: 'space-overview.smart-space-overview.popular-questions.sources-count',
		defaultMessage: '{count, plural, one {# Source} other {# Sources}}',
		description: 'Button label showing the number of sources for a popular question.',
	},
	expandSources: {
		id: 'space-overview.smart-space-overview.popular-questions.expand-sources',
		defaultMessage: 'Expand sources',
		description: 'Accessible label for the button to expand sources.',
	},
	collapseSources: {
		id: 'space-overview.smart-space-overview.popular-questions.collapse-sources',
		defaultMessage: 'Collapse sources',
		description: 'Accessible label for the button to collapse sources.',
	},
	askRovoPlaceholder: {
		id: 'space-overview.smart-space-overview.popular-questions.ask-rovo-placeholder',
		defaultMessage: 'Ask Rovo anything',
		description: 'Placeholder text for the Rovo question input.',
	},
	sendQuestion: {
		id: 'space-overview.smart-space-overview.popular-questions.send-question',
		defaultMessage: 'Send question',
		description: 'Accessible label for the send question button.',
	},
});

const styles = cssMap({
	questionRow: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		paddingBlock: token('space.150'),
		paddingInline: token('space.200'),
		cursor: 'pointer',
		backgroundColor: token('color.background.neutral'),
		borderWidth: '0',
		width: '100%',
		font: token('font.body'),
		color: token('color.text'),
		textAlign: 'start',
		borderRadius: token('radius.small'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered'),
		},
	},
	questionRowExpanded: {
		borderEndStartRadius: '0',
		borderEndEndRadius: '0',
	},
	questionIcon: {
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	questionText: {
		flex: 1,
		minWidth: '0',
	},
	chevron: {
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	expandedContent: {
		backgroundColor: token('color.background.neutral'),
		borderEndStartRadius: token('radius.small'),
		borderEndEndRadius: token('radius.small'),
		paddingBlock: token('space.200'),
		paddingInline: token('space.200'),
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
	},
	sectionLabel: {
		paddingBlockEnd: token('space.050'),
	},
	sourcesDivider: {
		borderTopStyle: 'solid',
		borderTopWidth: token('border.width'),
		borderTopColor: token('color.border'),
		marginBlock: token('space.100'),
	},
	followUpButton: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: token('space.075'),
		paddingBlock: token('space.075'),
		paddingInline: token('space.150'),
		backgroundColor: token('color.background.neutral'),
		borderWidth: '0',
		borderRadius: token('radius.medium'),
		cursor: 'pointer',
		textAlign: 'start',
		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered'),
		},
	},
	rovoInputContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		paddingBlock: token('space.075'),
		paddingInlineStart: token('space.150'),
		paddingInlineEnd: token('space.075'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.medium'),
		backgroundColor: token('elevation.surface'),
	},
	rovoIcon: {
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
	},
	rovoInput: {
		flex: 1,
		borderWidth: '0',
		outline: 'none',
		backgroundColor: 'transparent',
		font: token('font.body'),
		color: token('color.text'),
		'&::placeholder': {
			color: token('color.text.subtlest'),
		},
	},
});
